/**
 * 游戏状态类型定义
 */
export enum GameStatus {
  NOT_STARTED = "NOT_STARTED", // 游戏未开始
  RUNNING = "RUNNING",         // 游戏进行中
  GAME_OVER = "GAME_OVER",     // 游戏结束
}

/**
 * 结局类型
 */
export enum EndingType {
  EARLY,      // 提前到达
  ON_TIME,    // 准时到达
  LATE,       // 迟到
  VERY_LATE,  // 严重迟到
  NEVER,      // 永远不会到达
  SPECIAL     // 特殊结局
}

/**
 * 结局情感分类枚举
 */
export enum EndingSentiment {
  POSITIVE = 'positive',    // 好结局 - 积极正面的体验
  NEGATIVE = 'negative',    // 坏结局 - 消极负面的体验  
  NEUTRAL = 'neutral'       // 中性结局 - 不好不坏的现实体验
}

/**
 * 特殊效果类型
 */
export enum SpecialEffectType {
  FORGOT_DOCUMENTS,      // 忘记带重要文件
  SPILLED_COFFEE,        // 衣服被咖啡弄脏
  MUDDY_SHOES,           // 鞋子沾满泥土
  RAIN_SOAKED,           // 被雨淋湿
  GREAT_MOOD,            // 心情极好
  BAD_MOOD,              // 心情糟糕
  SUPER_ENERGY,          // 精力充沛
  TIRED,                 // 疲惫不堪
  LUCKY_DAY,             // 幸运日
  BAD_LUCK_DAY,          // 霉运日
  ALIEN_TECHNOLOGY,      // 获得外星科技
  FLYING_ABILITY,        // 获得飞行能力
  BOSS_FAVOR,            // 老板青睐
  BOSS_ANGRY,            // 老板生气
  RETURN_HOME,           // 回家拿东西 (重置距离)
  HELPED_STRANGER,       // 帮助了陌生人
  ITEM_DAMAGE,           // 物品损坏 (例如手机)

  // New effects:
  FORGOT_SOMETHING_ANXIETY, // 怀疑忘带东西的焦虑
  MAJOR_FUMBLE,          // 重大失误
  PHONE_FORGOTTEN,       // 忘带手机
  SCUFFED_SHOES,         // 鞋子被刮花或踩脏
  ITEM_LOST,             // 物品丢失
  DIRTY_HANDS,           // 手脏了
  UNKNOWN_STAIN,         // 不明污渍 (用于鸟粪/滴水等)
  RELIEF,                // 如释重负
  DESPAIR,               // 绝望
  CONFIDENCE_BOOST,      // 自信提升
  SOCIAL_BATTERY_DRAIN,  // 社交能量消耗
  SLIGHTLY_ENCUMBERED,   // 轻微受累/有点碍事 (例如拿着一堆传单)
  
  // Missing effects found in events:
  STRESS_INCREASE,       // 压力增加
  OUT_OF_BREATH,         // 上气不接下气
  LATE_FOR_WORK,         // 迟到状态
  MOOD_CHANGE,           // 心情变化
  MINOR_STAIN,           // 轻微污渍
  CASUAL_ATTIRE          // 休闲装扮
}

/**
 * 事件触发条件类型
 */
export enum ConditionType {
  TIME,           // 时间条件
  DISTANCE,       // 距离条件
  SPECIAL_EFFECT, // 特殊效果条件
  EVENT_COUNT     // 事件数量条件
}

/**
 * 事件触发条件接口
 */
export interface EventCondition {
  type: ConditionType;              // 条件类型
  parameter: string | number;       // 条件参数（可以是效果类型或数值）
  comparison: 'eq' | 'gt' | 'lt' | 'gte' | 'lte'; // 比较操作: 等于、大于、小于、大于等于、小于等于
}

/**
 * 特殊效果接口
 */
export interface SpecialEffect {
  type: SpecialEffectType;   // 特殊效果类型
  description: string;       // 效果描述
  severity?: number;         // 效果强度（1-5）
}

/**
 * 可能的结果接口
 */
export interface PossibleOutcome {
  minTimeChange: number;         // 最小时间变化（分钟）
  maxTimeChange: number;         // 最大时间变化（分钟）
  minDistanceChange: number;     // 最小距离变化（公里）
  maxDistanceChange: number;     // 最大距离变化（公里）
  resultMessage?: string;        // 结果消息
  weight?: number;               // 概率权重（默认为1）
  specialEffect?: SpecialEffect; // 特殊效果（可选）
}

/**
 * 延迟触发事件接口
 */
export interface DelayedEvent {
  eventId: string;           // 事件ID
  delay: number;             // 延迟几个事件后触发
  conditions?: EventCondition[]; // 触发条件（可选）
}

/**
 * 事件选项接口
 */
export interface Choice {
  id: string;                  // 选项ID
  text: string;                // 选项文本
  timeChange?: number;         // 时间变化（分钟）
  distanceChange?: number;     // 距离变化（公里）
  resultMessage?: string;      // 结果消息
  possibleOutcomes?: PossibleOutcome[]; // 可能的多种结果
  specialEffect?: SpecialEffect; // 特殊效果（可选）
  nextEventId?: string;        // 下一个特定事件的ID（可选）
  nextEventProbability?: number; // 触发下一个事件的概率（0-1）
  nextEventConditions?: EventCondition[]; // 触发下一个事件的条件（可选）
  nextEventDelay?: number;     // 延迟几个事件后触发（可选）
}

/**
 * 事件结果接口
 */
export interface EventResult {
  timeChange: number;           // 时间变化（分钟）
  distanceChange: number;       // 距离变化（公里）
  resultMessage: string;        // 结果消息
  specialEffect?: SpecialEffect; // 特殊效果（可选）
}

/**
 * 特殊结局接口
 */
export interface SpecialEnding {
  title: string;                 // 结局标题
  description: string;           // 结局描述
  requiredEffects: SpecialEffectType[]; // 所需特殊效果
  priority: number;              // 优先级（高优先级结局会覆盖低优先级结局）
  sentiment: EndingSentiment;     // 结局情感分类
}

/**
 * 事件接口
 */
export interface Event {
  id: string;            // 事件ID
  title: string;         // 事件标题
  description: string;   // 事件描述
  imageUrl?: string;     // 事件图片URL（可选）
  choices: Choice[];     // 可用选项
  weight?: number;       // 事件权重（影响出现概率，可选）
  tags?: string[];       // 事件标签（可选）
  minDistance?: number;  // 最小距离要求（可选）
  maxDistance?: number;  // 最大距离要求（可选）
  conditions?: EventCondition[]; // 新增：事件出现条件（可选）
}

/**
 * 游戏状态接口
 */
export interface GameState {
  status: GameStatus;               // 游戏状态
  currentTime: number;              // 当前时间（分钟，从480开始=8:00 AM）
  distanceToWork: number;           // 到公司的剩余距离（公里）
  totalDistance: number;            // 总距离（公里）
  eventsEncountered: Set<string>;      // 修改: 已遇到的事件ID列表 (使用 Set)
  currentEvent?: Event;             // 当前事件
  specialEffects: SpecialEffect[];  // 特殊效果列表
  delayedEvents?: DelayedEvent[];   // 延迟触发的事件列表
}

/**
 * 游戏配置接口
 */
export interface GameConfig {
  startTime: number;       // 开始时间（分钟，默认480=8:00 AM）
  workStartTime: number;   // 上班时间（分钟，默认540=9:00 AM）
  totalDistance: number;   // 总距离（公里，默认20）
  minEventsCount: number;  // 最少事件数量
  maxEventsCount: number;  // 最多事件数量
  distanceChangeFactor?: number; // 新增：距离变化因子
}

/**
 * 事件管理器接口
 */
export interface EventSystem {
  loadEvents(): Promise<void>;           // 加载事件
  getRandomEvent(state: GameState): Event | null;  // 获取随机事件
  getEventById(id: string): Event | null;  // 通过ID获取事件
}

/**
 * 状态管理器接口
 */
export interface StateManager {
  getState(): GameState;                     // 获取当前状态
  updateTime(minutes: number): void;         // 更新时间
  updateDistance(km: number): void;          // 更新距离
  setCurrentEvent(event: Event | null): void;  // 设置当前事件
  addSeenEvent(eventId: string): void;  // 修改/新增: 添加已遇到事件
  getSeenEvents(): Set<string>;         // 新增: 获取已遇到的事件集合
  addSpecialEffect(effect: SpecialEffect): void; // 添加特殊效果
  addDelayedEvent(event: DelayedEvent): void;   // 添加延迟触发的事件
  getNextDelayedEvent(): DelayedEvent | null;    // 获取下一个应该触发的延迟事件
  updateDelayedEvents(): void;                   // 更新延迟事件的计数
  checkEventConditions(conditions: EventCondition[]): boolean; // 检查事件条件是否满足
  checkGameOver(): GameStatus;               // 检查游戏是否结束
  getEndingType(): EndingType;               // 获取结局类型
  getSpecialEnding(): SpecialEnding | null;  // 获取特殊结局
  resetGame(): void;                         // 重置游戏
}

/**
 * 游戏引擎接口
 */
export interface GameEngine {
  startGame(): void;                                  // 开始游戏
  triggerNextEvent(): boolean;                        // 触发下一个事件
  processChoice(choiceIndex: number): EventResult | undefined; // 处理选择
} 